import { z } from 'zod';
import {
  CLASS_SOCIETIES,
  SENSITIVITY_LEVELS,
  VESSEL_CATEGORIES,
} from './types';

/**
 * Single source of truth for vessel form validation.
 *
 * `vesselFormSchema` matches the create payload (name + companyId
 * required, the rest optional). `vesselUpdateSchema` is derived via
 * `.partial()` so PATCH requests can carry just the fields that
 * actually changed.
 *
 * Optional string fields use `transform` to normalise empty strings
 * into `null`, matching what the backend expects (and what TypeScript
 * sees for unchanged optional columns).
 */

const optionalString = (max: number) =>
  z
    .string()
    .max(max)
    .optional()
    .transform((v) => (v === undefined || v === '' ? null : v));

const optionalNumber = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  });

export const vesselFormSchema = z.object({
  name: z.string().min(1).max(200),
  companyId: z.string().uuid(),

  imo: z
    .string()
    .regex(/^\d{7}$/, 'IMO must be exactly 7 digits')
    .or(z.literal(''))
    .optional()
    .transform((v) => (v === undefined || v === '' ? null : v)),
  mmsi: optionalString(9),
  callSign: optionalString(20),
  flag: optionalString(2),

  category: z.enum(VESSEL_CATEGORIES).default('civilian'),
  sensitivityLevel: z.enum(SENSITIVITY_LEVELS).default('unclassified'),
  nsnOtan: optionalString(20),
  displayLabel: optionalString(100),

  lengthM: optionalNumber,
  beamM: optionalNumber,
  grossTonnage: optionalNumber,
  yearBuilt: optionalNumber,
  shipyard: optionalString(200),

  classSociety: z
    .enum(CLASS_SOCIETIES)
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  classNumber: optionalString(50),
  classSurveyed: z.boolean().default(false),

  homePort: optionalString(100),
  isActive: z.boolean().default(true),
});

export type VesselFormValues = z.infer<typeof vesselFormSchema>;

/** PATCH-friendly: every field optional, same constraints when present. */
export const vesselUpdateSchema = vesselFormSchema.partial();
export type VesselUpdateValues = z.infer<typeof vesselUpdateSchema>;
